/**
 * GAME OPTIONS ENGINE
 * Unified engine for:
 * 1. Building brand settings UI dynamically from GAME_CONFIGS
 * 2. Reading, applying, saving, and loading settings
 * 3. Enforcing non-overlapping placements on the board track
 * 4. Parameterized randomizer for all game variants
 * 5. Runtime active track items querying
 */

(function (root, factory) {
  if (typeof module === 'object' && module.exports) {
    module.exports = factory(require('./game-configs.js'));
  } else {
    root.GameOptionsEngine = factory(root.GAME_CONFIGS);
  }
})(typeof self !== 'undefined' ? self : this, function (GAME_CONFIGS) {
  'use strict';

  let _suppressSave = false;
  let _isEnforcingOverlap = false;

  const SIGN_SLOT_OPTIONS = [
    { value: 'Plus 5', text: 'Plus 5 (+5 pts)' },
    { value: 'Plus 10', text: 'Plus 10 (+10 pts)' },
    { value: 'Minus 5', text: 'Minus 5 (−5 pts)' },
    { value: 'Minus 10', text: 'Minus 10 (−10 pts)' },
    { value: 'Undo', text: 'Undo (Revert Last Score)' },
    { value: 'Go To Jail', text: 'Go To Jail' },
    { value: 'Go To The Bar', text: 'Go To The Bar' },
    { value: 'Go To Timeout', text: 'Go To Timeout' },
    { value: 'Score Plus x2', text: 'Score Plus x2 (2x Last Score)' },
    { value: 'Score Plus x3', text: 'Score Plus x3 (3x Last Score)' },
    { value: 'Score Minus x2', text: 'Score Minus x2 (−2x Last Score)' },
    { value: 'Score Minus x3', text: 'Score Minus x3 (−3x Last Score)' },
    { value: 'Roll The Dice', text: 'Roll The Dice (3 Dice)' },
    { value: 'Spin The Wheel', text: 'Spin The Wheel (8 Slots)' }
  ];

  function getAppBrand() {
    if (typeof window !== 'undefined') {
      if (window.currentBrand) return window.currentBrand;
      if (window.currentLogoBrand) return window.currentLogoBrand;
      if (typeof window.detectBrand === 'function') {
        try { return window.detectBrand(); } catch (e) {}
      }
    }
    return 'crazy';
  }

  function getStreetOrTurnName(h, targetVal) {
    targetVal = Number(targetVal || (typeof settings !== 'undefined' && settings && settings.target) || 121);
    const isClashTarget = [241, 361, 481].includes(targetVal);
    if (isClashTarget) {
      if (h <= 0) return 'Start';
      if (h >= targetVal) return 'Finish';
      const row = Math.floor((h - 1) / 60);
      const pos = (h - 1) % 60;
      if (pos < 55) return `Street ${row + 1}`;
      return `Turn ${row + 1}`;
    }
    if (h > 70) return 'Street 3';
    if (h > 65) return 'Turn 2';
    if (h > 35) return 'Street 2';
    if (h > 30) return 'Turn 1';
    return 'Street 1';
  }

  // ==========================================
  // 1. DYNAMIC HTML GENERATION
  // ==========================================

  function buildBrandSettingsUI(brand, containerEl) {
    const config = GAME_CONFIGS.getGame(brand);
    if (!config || !containerEl) return;

    let html = '';

    // Randomize Bar
    if (config.randomizer) {
      const rand = config.randomizer;
      const randBtnId = `btnRandomize_${brand}`;
      const fullBtnId = `btnFullRandom_${brand}`;

      html += `<div class="clash-randomize-bar">
        <div class="clash-randomize-btn-row">
          <button type="button" id="${randBtnId}" class="clash-randomize-btn" data-brand="${brand}" data-action="randomize">
            <span class="clash-rand-dice">🎲</span> ${rand.buttonText || 'Randomize'}
          </button>`;
      if (rand.fullRandomText) {
        html += `<button type="button" id="${fullBtnId}" class="clash-randomize-btn clash-full-random-btn" data-brand="${brand}" data-action="full-randomize">
            <span class="clash-rand-dice">⚡</span> ${rand.fullRandomText}
          </button>`;
      }
      html += `</div>`;
      if (rand.description) {
        html += `<div class="clash-randomize-desc">${rand.description}</div>`;
      }
      html += `</div>`;
    }

    // Render Subgroups and Options
    const subgroups = config.subgroups || [{ id: 'default', title: config.name }];
    subgroups.forEach(subgroup => {
      const groupOptions = config.options.filter(o => (o.subgroup || 'default') === subgroup.id);
      if (!groupOptions.length) return;

      if (subgroup.title) {
        html += `<div class="chk-subgroup-label">${subgroup.title}</div>`;
      }

      groupOptions.forEach(opt => {
        if (opt.type === 'boolean') {
          html += `<label class="chk ${opt.mutualGroup ? 'chk-indented' : ''}">
            <input type="checkbox" id="${opt.chkId}" data-opt-key="${opt.key}" data-brand="${brand}">
            ${opt.name} — ${opt.description}
          </label>`;
        } else if (opt.type === 'trackItem') {
          html += renderTrackItemMarkup(brand, opt);
        } else if (opt.type === 'skunkSpecial') {
          html += renderSkunkMarkersMarkup();
        }
      });
    });

    containerEl.innerHTML = html;
    populateBrandHoleDropdowns(brand);
    bindBrandContainerEvents(brand, containerEl);
  }

  function populateBrandHoleDropdowns(brand, targetVal) {
    const config = GAME_CONFIGS.getGame(brand);
    if (!config) return;
    targetVal = Number(targetVal || (typeof settings !== 'undefined' && settings && settings.target) || config.defaultTarget || 121);
    const isClashTarget = [241, 361, 481].includes(targetVal);

    config.options.filter(o => o.type === 'trackItem').forEach(opt => {
      const min = (opt.holes && opt.holes.min) || 5;
      let max = (opt.holes && opt.holes.max) || 120;
      if (brand === 'clash' && isClashTarget) {
        if (opt.key === 'noPassingLanes') max = targetVal - 11;
        else if (opt.key === 'jump') max = targetVal - 11;
        else if (opt.key === 'stopSign') max = targetVal - 6;
        else max = targetVal - 11;
      }
      const step = (opt.holes && opt.holes.step) || 5;
      const suffix = (opt.holes && opt.holes.suffix) || 'Hole';

      for (let i = 1; i <= opt.maxCount; i++) {
        const selId = `${opt.prefix}${i}${suffix}`;
        const sel = document.getElementById(selId);
        if (sel) {
          let optsHtml = '';
          for (let h = min; h <= max; h += step) {
            let label = `Hole ${h}`;
            if (opt.key === 'noPassingLanes') label = `Hole ${h} (${h}–${h + 4})`;
            optsHtml += `<option value="${h}">${label}</option>`;
          }
          sel.innerHTML = optsHtml;
          const defaultHole = (opt.holes && opt.holes.defaults && opt.holes.defaults[i - 1]) || (i * 15);
          sel.value = String(defaultHole);
        }

        if (opt.hasDestination) {
          const dSelId = `${opt.prefix}${i}${(opt.destHoles && opt.destHoles.suffix) || 'Dest'}`;
          const dSel = document.getElementById(dSelId);
          if (dSel) {
            let dOptsHtml = '';
            const dMax = (brand === 'clash' && isClashTarget) ? (targetVal - 1) : 120;
            for (let dh = 5; dh <= dMax; dh += 5) {
              dOptsHtml += `<option value="${dh}">Hole ${dh}</option>`;
            }
            dSel.innerHTML = dOptsHtml;
            const defaultDest = (opt.destHoles && opt.destHoles.defaults && opt.destHoles.defaults[i - 1]) || (defaultHole + 20);
            dSel.value = String(defaultDest);
          }
        }
      }
    });

    if (brand === 'clash') {
      const maxMarker = isClashTarget ? (targetVal - 1) : 120;
      ['optSkunkMarkerHole', 'optDoubleSkunkMarkerHole'].forEach(id => {
        const sel = document.getElementById(id);
        if (sel) {
          let mHtml = '';
          for (let h = 5; h <= maxMarker; h += 5) mHtml += `<option value="${h}">Hole ${h}</option>`;
          sel.innerHTML = mHtml;
        }
      });
      const skunkDefs = typeof getSkunkDefaults === 'function' ? getSkunkDefaults(targetVal) : { skunkHole: 360, doubleSkunkHole: 240 };
      const s1 = document.getElementById('optSkunkMarkerHole');
      const s2 = document.getElementById('optDoubleSkunkMarkerHole');
      if (s1) s1.value = String(skunkDefs.skunkHole);
      if (s2) s2.value = String(skunkDefs.doubleSkunkHole);
    }
  }

  function renderTrackItemMarkup(brand, opt) {
    const configId = `${opt.key}Config`;
    const iconSpan = opt.icon ? `<span style="margin-right:4px;">${opt.icon}</span>` : '';
    const titleColor = opt.color ? `style="color: ${opt.color};"` : '';

    let out = `<label class="chk">
      <input type="checkbox" id="${opt.chkId}" data-opt-key="${opt.key}" data-brand="${brand}" data-field-type="toggle">
      ${opt.name} — ${opt.description}
    </label>
    <div id="${configId}" class="no-passing-config pints-config adult-config ${brand === 'clash' ? '' : 'pints-config'}" style="display: none;" data-config-key="${opt.key}">
      <div class="npl-header">
        <span class="npl-title" ${titleColor}>${iconSpan}${opt.name} Setup</span>
        <span class="npl-sub" id="${opt.key}Sub">${opt.headerDesc || (opt.actionText ? '1 peg · ' + opt.actionText : '')}</span>
      </div>`;

    // Active Count Row
    out += `<div class="npl-row">
      <label for="${opt.countId}">Active ${opt.badgePrefix || 'Spaces'}:</label>
      <select id="${opt.countId}" data-opt-key="${opt.key}" data-brand="${brand}" data-field-type="count">`;
    for (let c = 1; c <= opt.maxCount; c++) {
      const selected = c === (opt.defaultCount || 2) ? ' selected' : '';
      const unit = c === 1 ? (opt.badgePrefix || 'Space') : (opt.badgePrefix ? opt.badgePrefix + 's' : 'Spaces');
      out += `<option value="${c}"${selected}>${c} ${unit}</option>`;
    }
    out += `</select></div>`;

    // Variable Peg Length Row (if applicable)
    if (opt.pegsId) {
      out += `<div class="npl-row" style="margin-top: 6px;">
        <label for="${opt.pegsId}">Number of Pegs:</label>
        <select id="${opt.pegsId}" data-opt-key="${opt.key}" data-brand="${brand}" data-field-type="pegs">`;
      for (let p = 1; p <= 5; p++) {
        const selected = p === (opt.defaultPegs || 1) ? ' selected' : '';
        out += `<option value="${p}"${selected}>${p} Peg${p > 1 ? 's' : ''}</option>`;
      }
      out += `</select></div>`;
    }

    // Rows List (1..maxCount)
    out += `<div class="npl-lanes-list">`;
    for (let i = 1; i <= opt.maxCount; i++) {
      const isVisible = i <= (opt.defaultCount || 2);
      const rowId = `${opt.key}Row${i}`;
      const badgeId = `${opt.key}Badge${i}`;
      const suffix = (opt.holes && opt.holes.suffix) || 'Hole';
      const holeSelId = `${opt.prefix}${i}${suffix}`;
      const labelText = `${opt.badgePrefix || opt.name} ${i}`;
      const defaultHole = (opt.holes && opt.holes.defaults && opt.holes.defaults[i - 1]) || (i * 15);

      let badgeStyle = '';
      if (opt.color) {
        badgeStyle = `style="background: ${hexToRgba(opt.color, 0.2)}; color: ${opt.color}; border: 1px solid ${hexToRgba(opt.color, 0.4)};"`;
      }

      if (opt.key === 'jump') {
        // Special Jump Row Layout
        const destSelId = `${opt.prefix}${i}${(opt.destHoles && opt.destHoles.suffix) || 'Dest'}`;
        const defaultDest = (opt.destHoles && opt.destHoles.defaults && opt.destHoles.defaults[i - 1]) || (defaultHole + 20);
        out += `<div class="jump-item-row" id="${rowId}" style="${isVisible ? '' : 'display: none;'}">
          <div class="jump-row-top">
            <span class="jump-label">${labelText}</span>
            <span class="jump-badge jump-badge-forward" id="${badgeId}">Forward ➔</span>
          </div>
          <div class="jump-controls-grid">
            <div class="jump-field">
              <label for="${holeSelId}">Trigger Zone:</label>
              <select id="${holeSelId}" class="jump-select" data-opt-key="${opt.key}" data-index="${i}" data-field-type="hole"></select>
            </div>
            <div class="jump-field">
              <label for="${destSelId}">Jump To:</label>
              <select id="${destSelId}" class="jump-select" data-opt-key="${opt.key}" data-index="${i}" data-field-type="jumpDest"></select>
            </div>
          </div>
        </div>`;
      } else if (opt.key === 'signs') {
        // Special Signs Block Layout
        const sizeSelId = `${opt.prefix}${i}Size`;
        const slotsWrapId = `signs${i}SlotsWrap`;
        out += `<div class="signs-item-row" id="${rowId}" style="${isVisible ? '' : 'display: none;'}">
          <div class="signs-row-top">
            <span class="signs-label">${labelText}</span>
            <span class="signs-badge" id="${badgeId}">Holes ${defaultHole}–${defaultHole + 2} · 2 Signs</span>
          </div>
          <div class="signs-controls-grid">
            <div class="signs-field">
              <label for="${holeSelId}">Start Hole:</label>
              <select id="${holeSelId}" class="signs-select" data-opt-key="${opt.key}" data-index="${i}" data-field-type="hole"></select>
            </div>
            <div class="signs-field">
              <label for="${sizeSelId}">Block Size:</label>
              <select id="${sizeSelId}" class="signs-select" data-opt-key="${opt.key}" data-index="${i}" data-field-type="signsSize">
                <option value="2"${i % 2 === 1 ? ' selected' : ''}>3 Pegs (2 Signs)</option>
                <option value="4"${i % 2 === 0 ? ' selected' : ''}>5 Pegs (4 Signs)</option>
              </select>
            </div>
          </div>
          <div class="signs-slots-grid" id="${slotsWrapId}">
            ${renderSignsSlots(i)}
          </div>
        </div>`;
      } else {
        // Standard Track Item Row
        out += `<div class="jail-item-row" id="${rowId}" style="${isVisible ? '' : 'display: none;'}">
          <div class="jail-row-top">
            <span class="jail-label">${labelText}</span>
            <span class="jail-badge" id="${badgeId}" ${badgeStyle}>Hole ${defaultHole}</span>
          </div>
          <div class="jail-controls-grid">
            <div class="jail-field">
              <label for="${holeSelId}">Hole:</label>
              <select id="${holeSelId}" class="jail-select" data-opt-key="${opt.key}" data-index="${i}" data-field-type="hole"></select>
            </div>
          </div>
        </div>`;
      }
    }
    out += `</div></div>`;
    return out;
  }

  function renderSignsSlots(blockIdx) {
    let slotsHtml = '';
    const defaultSigns = [
      ['Plus 5', 'Roll The Dice', 'Score Plus x2', 'Minus 5'],
      ['Plus 10', 'Minus 10', 'Spin The Wheel', 'Plus 5'],
      ['Plus 5', 'Score Plus x3', 'Go To Jail', 'Go To The Bar'],
      ['Plus 5', 'Minus 10', 'Roll The Dice', 'Go To Timeout']
    ];
    const def = defaultSigns[blockIdx - 1] || defaultSigns[0];

    for (let s = 1; s <= 4; s++) {
      const slotSelId = `optSigns${blockIdx}Slot${s}`;
      const wrapId = `signs${blockIdx}Slot${s}Wrap`;
      const isSlotVisible = s <= 2; // default 2 slots visible for size=2
      slotsHtml += `<div class="signs-slot-field" id="${wrapId}" style="${isSlotVisible ? '' : 'display: none;'}">
        <label>Sign ${s}:</label>
        <select id="${slotSelId}" class="signs-select" data-opt-key="signs" data-index="${blockIdx}" data-slot="${s}" data-field-type="signSlot">`;
      SIGN_SLOT_OPTIONS.forEach(opt => {
        const selected = opt.value === def[s - 1] ? ' selected' : '';
        slotsHtml += `<option value="${opt.value}"${selected}>${opt.text}</option>`;
      });
      slotsHtml += `</select></div>`;
    }
    return slotsHtml;
  }

  function renderSkunkMarkersMarkup() {
    return `<label class="chk"><input type="checkbox" id="optSkunkMarkers" data-opt-key="skunkMarkers" data-field-type="toggle"> Skunk Markers — Display interactive 75% and 50% Skunk Markers directly on the game board</label>
    <div id="skunkMarkersConfig" class="stop-sign-config" style="display: none;">
      <div class="npl-header">
        <span class="npl-title stop-sign-title">🦨 Skunk Markers Setup</span>
        <span class="npl-sub">Single-hole markers · 75% Skunk and 50% Double Skunk</span>
      </div>
      <div class="npl-lanes-list">
        <div class="stop-sign-item-row" id="skunkMarkerRow">
          <div class="stop-sign-row-top">
            <span class="skunk-marker-label">🦨 Skunk Marker (75% mark)</span>
            <span class="stop-sign-badge" id="skunkMarkerBadge">Hole 360</span>
          </div>
          <div class="stop-sign-controls-grid">
            <div class="stop-sign-field">
              <label for="optSkunkMarkerHole">Marker Hole:</label>
              <select id="optSkunkMarkerHole" class="stop-sign-select" data-opt-key="skunkMarkers" data-field-type="skunkSingle"></select>
            </div>
          </div>
        </div>
        <div class="stop-sign-item-row" id="doubleSkunkMarkerRow">
          <div class="stop-sign-row-top">
            <span class="skunk-marker-label">🦨🦨 Double Skunk Marker (50% mark)</span>
            <span class="stop-sign-badge" id="doubleSkunkMarkerBadge">Hole 240</span>
          </div>
          <div class="stop-sign-controls-grid">
            <div class="stop-sign-field">
              <label for="optDoubleSkunkMarkerHole">Marker Hole:</label>
              <select id="optDoubleSkunkMarkerHole" class="stop-sign-select" data-opt-key="skunkMarkers" data-field-type="skunkDouble"></select>
            </div>
          </div>
        </div>
      </div>
    </div>`;
  }

  function hexToRgba(hex, alpha) {
    if (!hex || hex[0] !== '#') return hex;
    const r = parseInt(hex.slice(1, 3), 16) || 0;
    const g = parseInt(hex.slice(3, 5), 16) || 0;
    const b = parseInt(hex.slice(5, 7), 16) || 0;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  // ==========================================
  // 2. EVENT BINDING & CHANGE HANDLING
  // ==========================================

  function bindBrandContainerEvents(brand, containerEl) {
    containerEl.addEventListener('click', function (e) {
      const btn = e.target.closest('button[data-action]');
      if (!btn) return;
      const action = btn.getAttribute('data-action');
      if (action === 'randomize') {
        randomizeBrandOptions(brand, false);
      } else if (action === 'full-randomize') {
        randomizeBrandOptions(brand, true);
      }
    });

    containerEl.addEventListener('change', function (e) {
      const target = e.target;
      const optKey = target.getAttribute('data-opt-key');
      const fieldType = target.getAttribute('data-field-type');
      if (!optKey) return;

      handleFieldChange(brand, optKey, fieldType, target);
    });
  }

  function handleFieldChange(brand, optKey, fieldType, target) {
    const config = GAME_CONFIGS.getGame(brand);
    const opt = GAME_CONFIGS.getOption(brand, optKey);

    if (fieldType === 'toggle') {
      const panel = document.getElementById(`${optKey}Config`);
      if (panel) {
        panel.style.display = target.checked ? 'block' : 'none';
      }
      if (opt && opt.type === 'trackItem') {
        updateTrackItemRows(optKey);
        enforceBrandNoOverlap(brand);
      }
    } else if (fieldType === 'count') {
      updateTrackItemRows(optKey);
      enforceBrandNoOverlap(brand);
    } else if (fieldType === 'pegs') {
      updatePegsLabel(optKey, target.value);
      enforceBrandNoOverlap(brand);
    } else if (fieldType === 'hole' || fieldType === 'jumpDest') {
      enforceBrandNoOverlap(brand, target.id);
      updateBadge(brand, optKey, target.getAttribute('data-index'));
    } else if (fieldType === 'signsSize') {
      const idx = target.getAttribute('data-index');
      const sz = parseInt(target.value, 10);
      const slot3 = document.getElementById(`signs${idx}Slot3Wrap`);
      const slot4 = document.getElementById(`signs${idx}Slot4Wrap`);
      if (slot3) slot3.style.display = sz === 4 ? 'flex' : 'none';
      if (slot4) slot4.style.display = sz === 4 ? 'flex' : 'none';
      enforceBrandNoOverlap(brand);
      updateBadge(brand, optKey, idx);
    }

    if (opt && opt.mutualGroup && target.checked) {
      config.options.filter(o => o.mutualGroup === opt.mutualGroup && o.key !== opt.key).forEach(other => {
        const otherEl = document.getElementById(other.chkId);
        if (otherEl) otherEl.checked = false;
      });
    }

    if (!_suppressSave) {
      saveBrandSettings(brand, readBrandSettings(brand));
    }
  }

  function updateTrackItemRows(optKey) {
    const countEl = document.getElementById(`opt${capitalize(optKey)}Count`);
    const count = countEl ? parseInt(countEl.value, 10) : 2;
    for (let i = 1; i <= 8; i++) {
      const row = document.getElementById(`${optKey}Row${i}`);
      if (row) row.style.display = i <= count ? 'flex' : 'none';
    }
  }

  function updatePegsLabel(optKey, pegsVal) {
    const sub = document.getElementById(`${optKey}Sub`);
    const opt = GAME_CONFIGS.getOption(getAppBrand(), optKey);
    if (!sub || !opt) return;
    const p = parseInt(pegsVal, 10) || 1;
    const baseAction = opt.actionText || opt.description || '';
    sub.textContent = `${p === 1 ? '1 peg' : p + ' pegs'} · ${baseAction}`;
  }

  function updateBadge(brand, optKey, idx) {
    const opt = GAME_CONFIGS.getOption(brand, optKey);
    if (!opt) return;
    const badge = document.getElementById(`${optKey}Badge${idx}`);
    if (!badge) return;

    const suffix = (opt.holes && opt.holes.suffix) || 'Hole';
    const holeEl = document.getElementById(`${opt.prefix}${idx}${suffix}`);
    const h = holeEl ? parseInt(holeEl.value, 10) : 0;
    if (isNaN(h) || h <= 0) return;

    const targetVal = (typeof settings !== 'undefined' && settings && settings.target) || 121;
    const street = getStreetOrTurnName(h, targetVal);

    if (optKey === 'jump') {
      const destEl = document.getElementById(`${opt.prefix}${idx}${(opt.destHoles && opt.destHoles.suffix) || 'Dest'}`);
      const d = destEl ? parseInt(destEl.value, 10) : 0;
      const isFwd = !isNaN(d) && d > h;
      badge.className = 'jump-badge ' + (isFwd ? 'jump-badge-forward' : 'jump-badge-backward');
      badge.textContent = isFwd ? 'Forward (1 hole) ➔' : 'Backward (3 holes) ⬅';
      const row = document.getElementById(`jumpRow${idx}`);
      if (row) row.classList.toggle('backward', !isFwd);
    } else if (optKey === 'signs') {
      const sizeEl = document.getElementById(`${opt.prefix}${idx}Size`);
      const sz = sizeEl ? parseInt(sizeEl.value, 10) : 2;
      const len = sz === 4 ? 5 : 3;
      badge.textContent = `${street} (Holes ${h}–${h + len - 1} · ${sz} Signs)`;
    } else {
      const pegsEl = document.getElementById(opt.pegsId);
      const pegs = pegsEl ? parseInt(pegsEl.value, 10) : (opt.fixedLength || 1);
      if (pegs > 1) {
        badge.textContent = `${street} · Holes ${h}–${h + pegs - 1} (${pegs} Pegs)`;
      } else {
        badge.textContent = `${street} (Hole ${h})`;
      }
    }
  }

  // ==========================================
  // 3. READ, APPLY, SAVE, LOAD SETTINGS
  // ==========================================

  function readBrandSettings(brand) {
    const config = GAME_CONFIGS.getGame(brand);
    const data = {};

    config.options.forEach(opt => {
      const chk = document.getElementById(opt.chkId);
      if (opt.type === 'boolean') {
        data[opt.key] = chk ? chk.checked : false;
        data[opt.chkId] = chk ? chk.checked : false;
      } else if (opt.type === 'trackItem') {
        data[opt.chkId] = chk ? chk.checked : false;
        const countEl = document.getElementById(opt.countId);
        if (countEl) {
          data[`${opt.key}Count`] = parseInt(countEl.value, 10) || opt.defaultCount;
        }
        if (opt.pegsId) {
          const pegsEl = document.getElementById(opt.pegsId);
          if (pegsEl) {
            data[`${opt.key}Pegs`] = parseInt(pegsEl.value, 10) || 1;
          }
        }
        // Holes Array
        const holes = [];
        const suffix = (opt.holes && opt.holes.suffix) || 'Hole';
        for (let i = 1; i <= opt.maxCount; i++) {
          const hEl = document.getElementById(`${opt.prefix}${i}${suffix}`);
          const fallback = (opt.holes && opt.holes.defaults && opt.holes.defaults[i - 1]) || (i * 15);
          holes.push(hEl ? (parseInt(hEl.value, 10) || fallback) : fallback);
        }
        data[`${opt.key}Holes`] = holes;
        data[`${opt.key}Starts`] = holes; // alias for clash starts

        if (opt.key === 'noPassingLanes') {
          data.noPassingLaneCount = data.noPassingLanesCount;
          data.noPassingLaneStarts = holes;
          data.noPassingLaneHoles = holes;
        }

        // Jump Destinations
        if (opt.hasDestination) {
          const dests = [];
          const dSuffix = (opt.destHoles && opt.destHoles.suffix) || 'Dest';
          for (let i = 1; i <= opt.maxCount; i++) {
            const dEl = document.getElementById(`${opt.prefix}${i}${dSuffix}`);
            const fallback = (opt.destHoles && opt.destHoles.defaults && opt.destHoles.defaults[i - 1]) || (holes[i - 1] + 20);
            dests.push(dEl ? (parseInt(dEl.value, 10) || fallback) : fallback);
          }
          data[`${opt.key}Dests`] = dests;
        }

        // Signs Slots
        if (opt.hasSignsSlots) {
          const blocks = [];
          for (let i = 1; i <= opt.maxCount; i++) {
            const sizeEl = document.getElementById(`${opt.prefix}${i}Size`);
            const sz = sizeEl ? parseInt(sizeEl.value, 10) : 2;
            const signs = [];
            for (let s = 1; s <= sz; s++) {
              const sEl = document.getElementById(`optSigns${i}Slot${s}`);
              if (sEl && sEl.value) signs.push(sEl.value);
            }
            blocks.push({ id: i, start: holes[i - 1], size: sz, signs });
          }
          data.signsBlocks = blocks;
        }
      } else if (opt.type === 'skunkSpecial') {
        data[opt.chkId] = chk ? chk.checked : false;
        data.optSkunkMarkers = chk ? chk.checked : false;
        const skunkHoleEl = document.getElementById('optSkunkMarkerHole');
        const dblSkunkHoleEl = document.getElementById('optDoubleSkunkMarkerHole');
        if (skunkHoleEl) data.skunkMarkerHole = parseInt(skunkHoleEl.value, 10);
        if (dblSkunkHoleEl) data.doubleSkunkMarkerHole = parseInt(dblSkunkHoleEl.value, 10);
      }
    });

    return data;
  }

  function applyBrandSettings(brand, saved) {
    if (!saved) return;
    const config = GAME_CONFIGS.getGame(brand);
    _suppressSave = true;

    try {
      config.options.forEach(opt => {
        const chk = document.getElementById(opt.chkId);
        if (opt.type === 'boolean') {
          if (chk && saved[opt.key] !== undefined) chk.checked = Boolean(saved[opt.key]);
          else if (chk && saved[opt.chkId] !== undefined) chk.checked = Boolean(saved[opt.chkId]);
        } else if (opt.type === 'skunkSpecial') {
          const isEnabled = saved.optSkunkMarkers !== undefined ? Boolean(saved.optSkunkMarkers) : (saved[opt.chkId] !== undefined ? Boolean(saved[opt.chkId]) : false);
          if (chk) chk.checked = isEnabled;
          const panel = document.getElementById('skunkMarkersConfig');
          if (panel) panel.style.display = isEnabled ? 'block' : 'none';
          const skunkHoleEl = document.getElementById('optSkunkMarkerHole');
          if (skunkHoleEl && saved.skunkMarkerHole != null) skunkHoleEl.value = String(saved.skunkMarkerHole);
          const dblSkunkHoleEl = document.getElementById('optDoubleSkunkMarkerHole');
          if (dblSkunkHoleEl && saved.doubleSkunkMarkerHole != null) dblSkunkHoleEl.value = String(saved.doubleSkunkMarkerHole);
          const badge1 = document.getElementById('skunkMarkerBadge');
          if (badge1 && saved.skunkMarkerHole != null) badge1.textContent = `Hole ${saved.skunkMarkerHole}`;
          const badge2 = document.getElementById('doubleSkunkMarkerBadge');
          if (badge2 && saved.doubleSkunkMarkerHole != null) badge2.textContent = `Hole ${saved.doubleSkunkMarkerHole}`;
        } else if (opt.type === 'trackItem') {
          const isEnabled = saved[opt.chkId] !== undefined ? Boolean(saved[opt.chkId]) : (saved[opt.key] !== undefined ? Boolean(saved[opt.key]) : false);
          if (chk) chk.checked = isEnabled;
          const panel = document.getElementById(`${opt.key}Config`);
          if (panel) panel.style.display = isEnabled ? 'block' : 'none';

          // Count
          const count = saved[`${opt.key}Count`] || (opt.key === 'noPassingLanes' ? saved.noPassingLaneCount : null) || opt.defaultCount || 2;
          const countEl = document.getElementById(opt.countId);
          if (countEl) countEl.value = String(count);

          // Pegs
          if (opt.pegsId) {
            const pegs = saved[`${opt.key}Pegs`] || opt.defaultPegs || 1;
            const pegsEl = document.getElementById(opt.pegsId);
            if (pegsEl) pegsEl.value = String(pegs);
            updatePegsLabel(opt.key, pegs);
          }

          // Holes
          const holes = saved[`${opt.key}Holes`] || saved[`${opt.key}Starts`] || (opt.key === 'noPassingLanes' ? (saved.noPassingLaneHoles || saved.noPassingLaneStarts) : null) || (opt.holes && opt.holes.defaults) || [];
          const suffix = (opt.holes && opt.holes.suffix) || 'Hole';
          for (let i = 1; i <= opt.maxCount; i++) {
            const hEl = document.getElementById(`${opt.prefix}${i}${suffix}`);
            if (hEl && holes[i - 1] != null) {
              hEl.value = String(holes[i - 1]);
              hEl.dataset.customized = 'true';
            }
          }

          // Destination holes for Jump
          if (opt.hasDestination) {
            const dests = saved[`${opt.key}Dests`] || (opt.destHoles && opt.destHoles.defaults) || [];
            const dSuffix = (opt.destHoles && opt.destHoles.suffix) || 'Dest';
            for (let i = 1; i <= opt.maxCount; i++) {
              const dEl = document.getElementById(`${opt.prefix}${i}${dSuffix}`);
              if (dEl && dests[i - 1] != null) {
                dEl.value = String(dests[i - 1]);
                dEl.dataset.customized = 'true';
              }
            }
          }

          // Signs Blocks
          if (opt.hasSignsSlots && Array.isArray(saved.signsBlocks)) {
            saved.signsBlocks.forEach((block, idx) => {
              const i = idx + 1;
              const sizeEl = document.getElementById(`optSigns${i}Size`);
              if (sizeEl && block.size) sizeEl.value = String(block.size);
              const slot3 = document.getElementById(`signs${i}Slot3Wrap`);
              const slot4 = document.getElementById(`signs${i}Slot4Wrap`);
              if (slot3) slot3.style.display = block.size === 4 ? 'flex' : 'none';
              if (slot4) slot4.style.display = block.size === 4 ? 'flex' : 'none';

              if (Array.isArray(block.signs)) {
                block.signs.forEach((sgn, sIdx) => {
                  const sEl = document.getElementById(`optSigns${i}Slot${sIdx + 1}`);
                  if (sEl) sEl.value = sgn;
                });
              }
            });
          }

          updateTrackItemRows(opt.key);
          for (let i = 1; i <= opt.maxCount; i++) {
            updateBadge(brand, opt.key, i);
          }
        }
      });

      enforceBrandNoOverlap(brand);
    } finally {
      _suppressSave = false;
    }
  }

  function saveBrandSettings(brand, data) {
    if (_suppressSave) return;
    const config = GAME_CONFIGS.getGame(brand);
    try {
      localStorage.setItem(config.storageKey, JSON.stringify(data));
    } catch (e) {}
  }

  function loadBrandSettings(brand) {
    const config = GAME_CONFIGS.getGame(brand);
    try {
      const raw = localStorage.getItem(config.storageKey);
      if (raw) return JSON.parse(raw);
    } catch (e) {}
    return null;
  }

  // ==========================================
  // 4. NON-OVERLAPPING PLACEMENTS & COLLISIONS
  // ==========================================

  function getBrandActiveTrackItems(brand) {
    const config = GAME_CONFIGS.getGame(brand);
    const active = [];

    config.options.filter(o => o.type === 'trackItem').forEach(def => {
      const chk = document.getElementById(def.chkId);
      if (!chk || !chk.checked) return;

      const countEl = document.getElementById(def.countId);
      const count = countEl ? Math.min(def.maxCount, Math.max(1, parseInt(countEl.value, 10) || 1)) : 1;
      const suffix = (def.holes && def.holes.suffix) || 'Hole';

      for (let i = 1; i <= count; i++) {
        const selId = `${def.prefix}${i}${suffix}`;
        const sel = document.getElementById(selId);
        if (!sel) continue;
        const start = parseInt(sel.value, 10);
        if (isNaN(start) || start <= 0) continue;

        let length = 1;
        if (def.fixedLength) length = def.fixedLength;
        else if (def.pegsId) {
          const pEl = document.getElementById(def.pegsId);
          length = pEl ? parseInt(pEl.value, 10) || 1 : 1;
        } else if (def.lengthType === 'jumpDynamic') {
          const dEl = document.getElementById(`${def.prefix}${i}Dest`);
          const dVal = dEl ? parseInt(dEl.value, 10) : 0;
          length = (!isNaN(dVal) && dVal > start) ? 1 : 3;
        } else if (def.lengthType === 'signsSize') {
          const szEl = document.getElementById(`${def.prefix}${i}Size`);
          length = (szEl && parseInt(szEl.value, 10) === 4) ? 5 : 3;
        }

        active.push({
          brand,
          key: def.key,
          index: i,
          id: `${def.key}_${i}`,
          name: `${def.name} ${i}`,
          selectId: selId,
          start,
          length,
          end: start + length - 1,
          def
        });
      }
    });

    return active;
  }

  function enforceBrandNoOverlap(brand, triggeringSelectId) {
    if (_isEnforcingOverlap) return;
    _isEnforcingOverlap = true;

    try {
      const active = getBrandActiveTrackItems(brand);
      const occupied = new Map(); // hole -> item

      // First pass: mark non-colliding
      active.forEach(item => {
        for (let h = item.start; h <= item.end; h++) {
          if (occupied.has(h)) {
            // Collision! Shift this item to nearest free spot
            const freeStart = findFreeSlot(item.start, item.length, occupied, 5, 120, 5);
            item.start = freeStart;
            item.end = freeStart + item.length - 1;
            const sel = document.getElementById(item.selectId);
            if (sel) sel.value = String(freeStart);
            break;
          }
        }
        for (let h = item.start; h <= item.end; h++) {
          occupied.set(h, item);
        }
      });
    } finally {
      _isEnforcingOverlap = false;
    }
  }

  function findFreeSlot(desired, len, occupiedMap, minHole, maxHole, step) {
    function isFree(s) {
      for (let h = s; h < s + len; h++) {
        if (occupiedMap.has(h)) return false;
      }
      return true;
    }

    if (isFree(desired)) return desired;

    for (let delta = step; delta <= maxHole; delta += step) {
      if (desired + delta <= maxHole && isFree(desired + delta)) return desired + delta;
      if (desired - delta >= minHole && isFree(desired - delta)) return desired - delta;
    }
    for (let h = minHole; h <= maxHole; h += step) {
      if (isFree(h)) return h;
    }
    return desired;
  }

  // ==========================================
  // 5. UNIFIED RANDOMIZER
  // ==========================================

  function randomizeBrandOptions(brand, isFull = false) {
    const config = GAME_CONFIGS.getGame(brand);
    if (!config || !config.randomizer) return;

    if (config.randomizer.mode === 'subset') {
      // Crazy Cribbage: Randomize bonus checkboxes
      const container = document.getElementById('brand-game-options') || document.querySelector('.bonuses');
      if (!container) return;
      const chks = Array.from(container.querySelectorAll('input[type="checkbox"]'));
      chks.forEach(c => c.checked = false);

      // Pick 3 to 6 random bonuses
      const count = Math.floor(Math.random() * 4) + 3;
      const shuffled = chks.slice().sort(() => 0.5 - Math.random());
      shuffled.slice(0, count).forEach(c => c.checked = true);

      saveBrandSettings(brand, readBrandSettings(brand));
    } else if (config.randomizer.mode === 'trackDistribution') {
      // Pints & Adult: Distribute 24 track spaces across active items without overlap
      const trackOptions = config.options.filter(o => o.type === 'trackItem');
      trackOptions.forEach(opt => {
        const chk = document.getElementById(opt.chkId);
        if (chk) chk.checked = true;
        const panel = document.getElementById(`${opt.key}Config`);
        if (panel) panel.style.display = 'block';
      });

      const totalSpaces = config.randomizer.totalBoardSpaces || 24;
      const numOpts = trackOptions.length;
      const counts = new Array(numOpts).fill(1);
      let remaining = totalSpaces - numOpts;

      while (remaining > 0) {
        const candidates = [];
        for (let i = 0; i < numOpts; i++) {
          if (counts[i] < (trackOptions[i].maxCount || 4)) candidates.push(i);
        }
        if (!candidates.length) break;
        const chosen = candidates[Math.floor(Math.random() * candidates.length)];
        counts[chosen]++;
        remaining--;
      }

      // Distribute available track holes (5..120 in steps of 5)
      const availableHoles = [];
      for (let h = (config.randomizer.minHole || 5); h <= (config.randomizer.maxHole || 120); h += (config.randomizer.step || 5)) {
        availableHoles.push(h);
      }
      for (let i = availableHoles.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [availableHoles[i], availableHoles[j]] = [availableHoles[j], availableHoles[i]];
      }

      let holeIdx = 0;
      trackOptions.forEach((opt, idx) => {
        const count = counts[idx];
        const countSel = document.getElementById(opt.countId);
        if (countSel) countSel.value = String(count);

        if (opt.pegsId && isFull) {
          const pegsSel = document.getElementById(opt.pegsId);
          if (pegsSel) pegsSel.value = String(Math.floor(Math.random() * 5) + 1);
        }

        const suffix = (opt.holes && opt.holes.suffix) || 'Hole';
        for (let s = 1; s <= count; s++) {
          const sel = document.getElementById(`${opt.prefix}${s}${suffix}`);
          if (sel && holeIdx < availableHoles.length) {
            sel.value = String(availableHoles[holeIdx++]);
            sel.dataset.customized = 'true';
          }
        }
        updateTrackItemRows(opt.key);
        for (let s = 1; s <= opt.maxCount; s++) {
          updateBadge(brand, opt.key, s);
        }
      });

      enforceBrandNoOverlap(brand);
      saveBrandSettings(brand, readBrandSettings(brand));
    } else if (config.randomizer.mode === 'clashTactical') {
      // Delegate to existing or unified clash tactical solver
      if (typeof window.randomizeCribbageClashOptions === 'function') {
        window.randomizeCribbageClashOptions(isFull);
      }
    }
  }

  function capitalize(str) {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  function isCpuAllowed(brand) {
    const config = GAME_CONFIGS.getGame(brand);
    return config ? config.allowCpu !== false : true;
  }

  return {
    buildBrandSettingsUI,
    populateBrandHoleDropdowns,
    readBrandSettings,
    applyBrandSettings,
    saveBrandSettings,
    loadBrandSettings,
    getBrandActiveTrackItems,
    enforceBrandNoOverlap,
    randomizeBrandOptions,
    updateTrackItemRows,
    updateBadge,
    isCpuAllowed
  };
});
